import { useEffect, useMemo, useState } from "react";
import {
  BarChart3, Fuel, ReceiptText, Save, Settings, ShieldCheck, Ship, Users,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";

const STAT_ICON_OPTIONS = [
  { value: "Users", label: "Users", icon: Users },
  { value: "ReceiptText", label: "Receipt", icon: ReceiptText },
  { value: "Fuel", label: "Fuel", icon: Fuel },
  { value: "Ship", label: "Ship", icon: Ship },
  { value: "ShieldCheck", label: "Shield", icon: ShieldCheck },
  { value: "BarChart3", label: "Chart", icon: BarChart3 },
];

const DEFAULT_STAT_CARDS = [
  { key: "members", label: "Anggota Bergabung", icon: "Users" },
  { key: "transaction_value", label: "Perputaran Transaksi", icon: "ReceiptText" },
  { key: "fuel_liters", label: "Penyaluran BBM Kapal Nelayan", icon: "Fuel" },
  { key: "vessels", label: "Kapal Rekom BBM", icon: "Ship" },
];

const DEFAULT_FORM = {
  hero_kicker: "",
  hero_heading: "",
  hero_description: "",
  stat_cards: DEFAULT_STAT_CARDS,
};

export default function PageSettings() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const currentSnapshot = useMemo(() => JSON.stringify(form), [form]);
  const isDirty = currentSnapshot !== savedSnapshot;

  useEffect(() => {
    api.get("/settings/public-portal").then((r) => {
      const next = { ...r.data, stat_cards: r.data.stat_cards || DEFAULT_STAT_CARDS };
      setForm(next);
      setSavedSnapshot(JSON.stringify(next));
    }).catch(() => {
      setMessage("Gagal memuat pengaturan halaman.");
    }).finally(() => setLoading(false));
  }, []);

  const updateStatCard = (key, patch) => {
    setForm({
      ...form,
      stat_cards: (form.stat_cards || DEFAULT_STAT_CARDS).map((card) => (
        card.key === key ? { ...card, ...patch } : card
      )),
    });
    setMessage("");
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await api.put("/settings/public-portal", form);
      const next = { ...res.data, stat_cards: res.data.stat_cards || DEFAULT_STAT_CARDS };
      setForm(next);
      setSavedSnapshot(JSON.stringify(next));
      setMessage("Pengaturan halaman tersimpan.");
    } catch {
      setMessage("Gagal menyimpan pengaturan halaman.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="mono-label">Memuat...</p>;

  return (
    <div>
      <PageHeader
        kicker="Admin Koperasi"
        title="Pengaturan Halaman"
        desc="Kelola hero landing page dan stats footer yang tampil di halaman depan."
      />

      <section className="border hairline bg-white">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur p-5 sm:p-6 border-b hairline flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="mono-label">Landing Page</p>
            <h2 className="font-bold text-xl mt-1">Hero dan Stats Footer</h2>
            <p className="text-sm text-[var(--muted)] mt-1">
              {isDirty ? "Belum disimpan" : "Semua perubahan tersimpan"}
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || !isDirty}
            className="tap btn-primary px-5 font-semibold flex items-center justify-center gap-2 disabled:opacity-45"
          >
            <Save size={16} /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>

        <div className="p-5 sm:p-6 grid grid-cols-1 gap-3">
          <input
            className="field tap px-4"
            value={form.hero_kicker}
            onChange={(e) => { setForm({ ...form, hero_kicker: e.target.value }); setMessage(""); }}
            placeholder="Keterangan kecil di atas heading"
          />
          <textarea
            className="field px-4 py-3 min-h-[120px] text-2xl sm:text-3xl font-black leading-none"
            value={form.hero_heading}
            onChange={(e) => { setForm({ ...form, hero_heading: e.target.value }); setMessage(""); }}
            placeholder="Heading utama hero"
          />
          <textarea
            className="field px-4 py-3 min-h-[110px]"
            value={form.hero_description}
            onChange={(e) => { setForm({ ...form, hero_description: e.target.value }); setMessage(""); }}
            placeholder="Deskripsi di bawah heading"
          />

          <div className="mt-4 border-t hairline pt-5">
            <p className="mono-label mb-3">Stats Footer</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(form.stat_cards || DEFAULT_STAT_CARDS).map((card) => {
                const option = STAT_ICON_OPTIONS.find((item) => item.value === card.icon) || STAT_ICON_OPTIONS[0];
                const Icon = option.icon;
                return (
                  <div key={card.key} className="border hairline p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-[var(--sea)] text-white flex items-center justify-center">
                        <Icon size={16} />
                      </div>
                      <p className="mono-label">{card.key}</p>
                    </div>
                    <input
                      className="field tap px-4 w-full mb-3"
                      value={card.label}
                      onChange={(e) => updateStatCard(card.key, { label: e.target.value })}
                      placeholder="Nama stats"
                    />
                    <select
                      className="field tap px-4 w-full"
                      value={card.icon}
                      onChange={(e) => updateStatCard(card.key, { icon: e.target.value })}
                    >
                      {STAT_ICON_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {message && (
            <div className="flex items-center gap-2 text-sm text-[var(--muted)]">
              <Settings size={15} /> {message}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
